import React from 'react';
import { CheckCircle, Factory, Cpu, Wrench, Zap, Shield, Truck, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { manufacturingProcess } from '../mock/mockData';
import { Link } from 'react-router-dom';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';
import InternalLinks from '../components/seo/InternalLinks';

const ManufacturingFacility = () => {
  const iconMap = {
    wrench: Wrench,
    scissors: Wrench,
    flame: Zap,
    cpu: Cpu,
    package: Factory,
    monitor: Cpu,
    checkCircle: CheckCircle,
    shield: Shield,
    truck: Truck
  };

  return (
    <div className="min-h-screen pb-20 lg:pb-0">
      <SEOHead
        title="Our Manufacturing Facility | In-House Fabrication & Assembly | Alden Technologies"
        description="Tour our manufacturing facility: SS fabrication, laser cutting, argon welding, precision machining, assembly and PLC panel wiring — everything built in-house for quality control."
        path="/manufacturing-facility"
      />
      <Breadcrumbs items={[{ label: 'Manufacturing Facility' }]} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-alden-green to-alden-green-light text-white py-20 md:py-28">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block bg-white/20 px-6 py-2 rounded-full text-sm font-semibold mb-6">
            Designed. Fabricated. Tested. Delivered.
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Our Manufacturing Facility, Jaipur
          </h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto text-white/90 mb-8">
            In-house sheet fabrication, equipment assembly, quality inspection and factory testing — every Alden machine is built, tested and delivered from our Jaipur facility with PAN India installation and commissioning.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-center">
            <div className="bg-white/10 backdrop-blur-sm px-8 py-4 rounded-xl">
              <div className="text-3xl font-bold">15,000+</div>
              <div className="text-white/80">Sq. Ft. Facility</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-8 py-4 rounded-xl">
              <div className="text-3xl font-bold">100+</div>
              <div className="text-white/80">Machines Delivered</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-8 py-4 rounded-xl">
              <div className="text-3xl font-bold">50+</div>
              <div className="text-white/80">Skilled Engineers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Quality Standards</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              ISO-certified manufacturing processes ensure every machine meets international quality benchmarks.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { id: 'cert-iso', icon: Shield, title: 'ISO 9001:2015', desc: 'Quality Management' },
              { id: 'cert-bis', icon: Award, title: 'BIS Certified', desc: 'Indian Standards' },
              { id: 'cert-ce', icon: CheckCircle, title: 'CE Compliant', desc: 'European Safety' },
              { id: 'cert-food', icon: Factory, title: 'Food Grade', desc: 'SS 304/316 Material' }
            ].map((item) => (
              <Card key={item.id} className="text-center border-2 border-alden-green/20 hover:border-alden-green transition-all">
                <CardContent className="p-6">
                  <item.icon className="w-16 h-16 text-alden-green mx-auto mb-4" />
                  <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Manufacturing Process
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From raw material to final dispatch, every step is meticulously executed with precision engineering and quality control.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            {manufacturingProcess.map((process, idx) => {
              const Icon = iconMap[process.icon] || Factory;
              const isEven = idx % 2 === 0;
              
              return (
                <div key={process.id} className={`mb-12 flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}>
                  {/* Image */}
                  <div className="w-full md:w-1/2">
                    <div className="relative">
                      <div className="absolute -top-6 -left-6 bg-alden-green text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold shadow-lg z-10">
                        {idx + 1}
                      </div>
                      <img 
                        src={process.image} 
                        alt={process.title}
                        className="rounded-xl shadow-2xl w-full h-64 object-cover"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="w-full md:w-1/2">
                    <Card className="border-2 border-alden-green/30 shadow-lg">
                      <CardContent className="p-8">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-14 h-14 bg-alden-green/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon className="w-8 h-8 text-alden-green" />
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900">{process.title}</h3>
                        </div>
                        <p className="text-gray-700 mb-4 leading-relaxed">
                          {process.description}
                        </p>
                        <div className="bg-alden-green/5 p-4 rounded-lg border-l-4 border-alden-green">
                          <p className="text-sm text-gray-600">
                            <strong>Process Detail:</strong> {process.details}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Infrastructure Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Infrastructure Highlights
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                id: 'infra-cnc',
                title: 'CNC Machining Center',
                desc: '5-axis CNC machines for precision component manufacturing with 0.01mm accuracy.',
                image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837'
              },
              {
                id: 'infra-laser',
                title: 'Laser Cutting Unit',
                desc: 'Fiber laser cutting machines for precise SS sheet cutting with minimal wastage.',
                image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789'
              },
              {
                id: 'infra-assembly',
                title: 'Assembly Bay',
                desc: 'Dedicated assembly bays with overhead cranes for efficient machine assembly.',
                image: 'https://static.prod-images.emergentagent.com/jobs/932fe87d-11cd-44ee-9b8b-ca40c329d944/images/52870b21522d31e2f71037c6540604e73ca73f4f5ad0415bdddaa4945a301960.jpeg'
              }
            ].map((item) => (
              <Card key={item.id} className="overflow-hidden hover:shadow-xl transition-all">
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-alden-green to-alden-green-light text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Visit Our Manufacturing Facility
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Schedule a factory visit to see our manufacturing capabilities firsthand and meet our engineering team.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-alden-green hover:bg-gray-100 py-6 px-8 text-lg font-semibold">
                Book Factory Visit
              </Button>
            </Link>
            <a href="https://wa.me/919351292512?text=I%20would%20like%20to%20schedule%20a%20factory%20visit" target="_blank" rel="noopener noreferrer">
              <Button className="bg-alden-navy hover:bg-alden-navy-light text-white py-6 px-8 text-lg font-semibold">
                WhatsApp: +91 9351292512
              </Button>
            </a>
          </div>
        </div>
      </section>
      <InternalLinks currentPath="/manufacturing-facility" />
    </div>
  );
};

export default ManufacturingFacility;
